import { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomerStateIndicator, StateIndicator } from './customer-state-indicator';
import {
  Filter,
  DeprecatedSelectDropdown,
  DeprecatedDropdownItem,
  FilterDropdown,
  FilterDropdownItem,
} from '../../shared-components/atomic-components';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { customerStateMessages, teamFilterMessages } from '../../messages/filter-messages';
import { filterCustomers } from '@algoan/dashboard/store/thunks';
import { allTeamsValue } from '@algoan/dashboard/env';
import { CustomerTableSearch } from './customer-table-search';
import { customerFilterMessages } from './customer-filter-messages';
import { State, StateFamily } from '@algoan/dashboard/model';
import {
  filterSelector,
  isFilteringSelector,
  accessTokenTeamSelector,
  teamConfigSelector,
} from '@algoan/dashboard/store/selectors';
import { TeamIcon } from '../../shared-components/icons';

interface CustomItemProps {
  value: StateFamily;
  display: string;
}

const getStateIndicatorColor = (stateFamily: StateFamily): StateIndicator => {
  switch (stateFamily) {
    case StateFamily.AGGREGATION_STARTED:
      return StateIndicator.ORANGE;
    case StateFamily.IN_ERROR:
      return StateIndicator.RED;
    default:
      return StateIndicator.GREY;
  }
};

const CustomItem: React.FC<CustomItemProps> = ({ value, display }) => (
  <S.Item>
    <S.CustomerStateIndicator withMarginRight state={getStateIndicatorColor(value)} />
    {display}
  </S.Item>
);

const getStateFamilyText = (stateFamily: StateFamily): { id: string; defaultMessage: string; description: string } => {
  switch (stateFamily) {
    case StateFamily.FLOW_STARTED:
      return customerFilterMessages.flowStarted;
    case StateFamily.AGGREGATION_STARTED:
      return customerFilterMessages.aggregationStarted;
    default:
      return customerFilterMessages.inError;
  }
};

export const CustomerFilter: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const filterData = useSelector(filterSelector);
  const isFiltering = useSelector(isFilteringSelector);
  const teamConfigInformation = useSelector(teamConfigSelector);
  const team = useSelector(accessTokenTeamSelector);
  const [filtersWithoutAnalysisCustomers, setFiltersWithoutAnalysisCustomers] = useState<StateFamily[]>([]);

  const selectOptions = useMemo(
    (): Array<FilterDropdownItem<StateFamily>> => [
      { display: intl.formatMessage(customerFilterMessages.flowStarted), value: StateFamily.FLOW_STARTED },
      {
        display: intl.formatMessage(customerFilterMessages.aggregationStarted),
        value: StateFamily.AGGREGATION_STARTED,
      },
      { display: intl.formatMessage(customerFilterMessages.inError), value: StateFamily.IN_ERROR },
    ],
    [intl],
  );

  const handleItemRender = useCallback(
    ({ value, display }: { value: StateFamily; display: string }): React.ReactNode => (
      <CustomItem value={value} display={display} />
    ),
    [],
  );

  const onToggle = (option: StateFamily, isSelected: boolean): void => {
    if (isSelected) {
      dispatch(
        filterCustomers({
          hasAnalysis: false,
          stateFamilies: filterData.stateFamilies ? [...new Set([...filterData.stateFamilies, option])] : [option],
        }),
      );
      return;
    }

    const newStateFamilyArray = filterData.stateFamilies?.filter((element) => element !== option);
    dispatch(
      filterCustomers(
        newStateFamilyArray?.length === 0
          ? { hasAnalysis: undefined, stateFamilies: undefined }
          : { hasAnalysis: false, stateFamilies: newStateFamilyArray },
      ),
    );
  };

  const teamsDropdownOptions = teamConfigInformation?.teams.map((team) => ({ display: team.name, value: team.name }));
  teamsDropdownOptions?.unshift({ display: intl.formatMessage(teamFilterMessages.allTeams), value: allTeamsValue });

  const isWithAnalysisFilterSelected = filterData.hasAnalysis === true;
  const isWithoutAnalysisFilterSelected = filterData.hasAnalysis === false;

  useEffect(() => {
    setFiltersWithoutAnalysisCustomers(filterData.stateFamilies || []);
  }, [filterData]);

  const handleClickStatus = (state: State): void => {
    const previousStates = filterData.states;
    const newStates = previousStates.includes(state)
      ? previousStates.filter((prevState) => prevState !== state)
      : previousStates.concat(state);
    dispatch(filterCustomers({ states: newStates.length === 3 ? [] : newStates }));
  };

  const selectAllStatutes = (): boolean => {
    return filterData.states.length === 0 || filterData.states.length === 3;
  };

  const selectAllStates = (): boolean => {
    return filterData.hasAnalysis === undefined;
  };

  const renderFilterStatus = (state: State): React.ReactNode => {
    return (
      <Filter
        onClick={() => handleClickStatus(state)}
        selected={filterData.states.includes(state)}
        disabled={isFiltering}
        withCross={filterData.states.includes(state)}
        border={true}
      >
        {intl.formatMessage(customerStateMessages[state])}
      </Filter>
    );
  };

  const mapTeamDropdownValueToDisplayString = (value: string): string =>
    value === allTeamsValue ? intl.formatMessage(teamFilterMessages.allTeams) : value;

  const areAllStatesSelected = selectAllStates();
  const areAllStatutesSelected = selectAllStatutes();

  return (
    <S.Container>
      {(!areAllStatesSelected || !areAllStatutesSelected) && (
        <S.DeselectAllFiltersButton
          onClick={() => {
            dispatch(
              filterCustomers({
                hasAnalysis: isWithAnalysisFilterSelected && isWithoutAnalysisFilterSelected ? true : undefined,
                stateFamilies: undefined,
                states: [],
              }),
            );
            setFiltersWithoutAnalysisCustomers([]);
          }}
        >
          {intl.formatMessage(customerFilterMessages.deselectAllFilters)}
        </S.DeselectAllFiltersButton>
      )}
      <S.CustomerTableSearchContainer>
        <CustomerTableSearch />
      </S.CustomerTableSearchContainer>
      <S.FilterTitle>
        <FormattedMessage
          id="customer_filter.state_filter"
          defaultMessage="Application state"
          description="The title of the customer state filter"
        />
      </S.FilterTitle>
      <S.FilterRow isLargeGap>
        <S.FilterText>Filtrer par</S.FilterText>
        <Filter
          selected={isWithAnalysisFilterSelected}
          onClick={() =>
            isWithoutAnalysisFilterSelected
              ? dispatch(filterCustomers({ hasAnalysis: undefined, stateFamilies: undefined }))
              : dispatch(
                  filterCustomers({
                    hasAnalysis: isWithAnalysisFilterSelected || isWithoutAnalysisFilterSelected ? undefined : true,
                    stateFamilies: undefined,
                  }),
                )
          }
          disabled={isFiltering}
          withCross={isWithAnalysisFilterSelected}
          border={true}
        >
          <S.CustomerStateIndicator state={StateIndicator.GREEN} />
          <S.FilterLabel selected={isWithAnalysisFilterSelected}>
            <FormattedMessage
              id="customer_filter.with_analysis"
              defaultMessage="Analysis available"
              description="The label of customers with analysis available"
            />
          </S.FilterLabel>
        </Filter>
        <FilterDropdown
          options={selectOptions}
          label={intl.formatMessage(customerFilterMessages.withoutAnalysis)}
          onToggle={onToggle}
          selectedOptions={selectOptions.filter((option) => filtersWithoutAnalysisCustomers?.includes(option.value))}
          itemRenderer={handleItemRender}
        />
      </S.FilterRow>
      {filterData?.stateFamilies && filterData.stateFamilies?.length > 0 && (
        <S.StateFamilyButtonContainer>
          {filterData.stateFamilies.map((stateFamilyValue, index) => (
            <Filter
              key={`StateFamily-${stateFamilyValue}-${index}`}
              selected
              onClick={() => {
                const newStateFamilyArray = filterData.stateFamilies?.filter((element) => element !== stateFamilyValue);
                dispatch(
                  filterCustomers(
                    newStateFamilyArray?.length === 0
                      ? { hasAnalysis: undefined, stateFamilies: undefined }
                      : { hasAnalysis: false, stateFamilies: newStateFamilyArray },
                  ),
                );
              }}
              withCross
              border={true}
            >
              <S.StateFamilyTextContainer>
                <S.CustomerStateIndicator withMarginRight state={getStateIndicatorColor(stateFamilyValue)} />
                <S.FilterLabel selected>{intl.formatMessage(getStateFamilyText(stateFamilyValue))}</S.FilterLabel>
              </S.StateFamilyTextContainer>
            </Filter>
          ))}
        </S.StateFamilyButtonContainer>
      )}
      <S.FilterTitle>
        <FormattedMessage
          id="customer_filter.status"
          defaultMessage="Application status"
          description="The label of current customer state"
        />
      </S.FilterTitle>
      <S.FilterRow>
        <S.FilterText>Filtrer par</S.FilterText>
        {renderFilterStatus(State.NEW)}
        {renderFilterStatus(State.VIEWED)}
        {renderFilterStatus(State.ARCHIVED)}
      </S.FilterRow>

      {!team && teamsDropdownOptions && (
        <>
          <S.FilterTitle>
            <FormattedMessage
              id="customer_filter.team_filter"
              defaultMessage="Team"
              description="The title of the customers filter"
            />
          </S.FilterTitle>
          <S.FilterRow>
            <DeprecatedSelectDropdown
              onClick={(selectedOption: DeprecatedDropdownItem) => {
                dispatch(filterCustomers({ team: selectedOption.value }));
              }}
              options={teamsDropdownOptions}
              value={{ value: filterData.team, display: mapTeamDropdownValueToDisplayString(filterData.team) }}
              variant="secondary"
              testAttribute={'team-filter-select-dropdown'}
              itemRenderer={(option: DeprecatedDropdownItem) => {
                return (
                  <S.ItemContainer>
                    <S.TeamIconContainer>
                      <TeamIcon
                        color={teamConfigInformation?.teams.find((team) => team.name === option.value)?.color}
                      />
                    </S.TeamIconContainer>
                    <S.TeamLabelContainer>{option.display}</S.TeamLabelContainer>
                  </S.ItemContainer>
                );
              }}
            />
          </S.FilterRow>
        </>
      )}
    </S.Container>
  );
};

const FilterLabel = styled.div<{ selected?: boolean }>`
  color: ${(props) => (props.selected ? props.theme.main.deepBlue : props.theme.secondary.softBlue4)};
  font-size: 12px;
  line-height: 14px;
  white-space: nowrap;
`;

const S = {
  Container: styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    margin: auto;
    width: 100%;
    max-width: 841px;
  `,
  CustomerTableSearchContainer: styled.div`
    margin-bottom: 8px;
  `,
  FilterRow: styled.div<{ isLargeGap?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    gap: ${({ isLargeGap }) => (isLargeGap ? '24px' : '8px')};
  `,
  FilterText: styled.div`
    color: ${(props) => props.theme.algoanBlue};
    font-size: 12px;
    font-weight: ${(props) => props.theme.fontWeight.medium};
  `,
  FilterTitle: styled.div`
    color: ${(props) => props.theme.algoanBlue};
    font-size: 13px;
    font-weight: ${(props) => props.theme.fontWeight.regular};
    margin: 16px 0 8px 0;
  `,
  FilterLabel,
  FilterAllLabel: styled(FilterLabel)`
    ${({ selected }) => (selected ? 'font-weight: 700;' : '')}
  `,
  TeamIconContainer: styled.div`
    display: flex;
  `,
  TeamIcon: styled.div``,
  TeamLabelContainer: styled.div`
    margin-left: 10px;
  `,
  ItemContainer: styled.div`
    display: flex;
    margin: auto 0;
  `,
  CustomerStateIndicator: styled(CustomerStateIndicator)<{ withMarginRight?: boolean }>`
    ${({ withMarginRight }) => (withMarginRight ? 'margin-right: 6px;' : 'margin-top: 3px;')}
  `,
  Item: styled.div`
    display: flex;
    align-items: center;
    width: 160px;
  `,
  DeselectAllFiltersButton: styled.div`
    position: absolute;
    top: 64px;
    right: 0px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: ${({ theme }) => theme.secondary.softBlue1};
    cursor: pointer;

    &:hover {
      color: ${(props) => props.theme.secondary.softBlue2};
    }
  `,
  StateFamilyButtonContainer: styled.div`
    display: flex;
    gap: 8px;
    margin-top: 18px;
    height: 24px;
    width: 100%;
  `,
  StateFamilyTextContainer: styled.div`
    display: flex;
    align-items: center;
  `,
};
